import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCapabilities } from "../auth/capabilities";
import { TenantIdentity } from "../auth/tenantPicker";
import { AppShell } from "./AppShell";
import { NavIcon, type NavIconName } from "./navIcons";

// One nav entry: the icon, the label, and the SAME translated string as the link's
// `title`. The rail clips the label to zero width (shell.css) rather than removing it —
// the label stays the link's accessible name in both states — and the `title` is what
// gives a sighted user those words back on hover while collapsed. Going through one
// component is what keeps the two provably the same string.
//
// The `t("nav.*")` calls stay at the call sites on purpose: src/i18n/app-layout-i18n.test.mjs
// scans this file for literal keys, and a `t(labelKey)` inside here would hide every
// nav key from that guardrail.
function NavEntry({
  to,
  icon,
  label,
  end,
  "data-testid": testId,
}: {
  to: string;
  icon: NavIconName;
  label: string;
  end?: boolean;
  "data-testid"?: string;
}) {
  return (
    <NavLink to={to} end={end} title={label} data-testid={testId}>
      <NavIcon name={icon} />
      <span className="app-nav__label">{label}</span>
    </NavLink>
  );
}

// The PRODUCT's navigation, inside the shared app chrome (./AppShell, which carries the sidebar, the
// top bar, the page heading and the responsive drawer for this entry and for the operator console
// beside it — ADR-0014 §6 phase 2).
//
// Nav entries are gated by the caller's role capabilities (src/auth/capabilities.ts) so a learner only
// sees "Omat koulutukset" and never the admin surfaces; the same capabilities guard the routes
// (RequireRole) so hiding a link is not the only defence.
//
// There is no "Alusta" section here any more: the platform operator's console is its own entry on its
// own host with its own Keycloak client (apps/platform, ADR-0014 §6 phase 2). The customer's own
// pääkäyttäjä (ADMIN) surfaces below are untouched by that move.
//
// GROUPED BY SUBJECT since the owner's decision of 3.11.2026 (task c4a71fc1), and that decision is about
// one row in particular. "Sisällönhallinta" opened a page carrying BOTH the courses and the
// questionnaires, so the rail named a container of the product's internals rather than either of the two
// things a reader came for — and the sections around it were named after the AUTHORITY a row sits behind
// ("Hallinta", "Raportointi"), which asks the reader to know their own role before they can find a
// screen. The six sections below are named after the SUBJECT instead: Kurssit and Kyselyt are two rows
// leading to two pages, and what belongs to each stands under it (Määrää koulutus under Koulutukset,
// Kyselyn tulokset under Kyselyt).
//
// WHAT DID NOT CHANGE, and both halves are load-bearing:
//   * THE ROUTES. Every `to=` below is the path that row already had, so a bookmark and a mailed link
//     still land where they did. The one route that MOVED is the Kyselyt LIST, and it moved back: it
//     answered /admin/questionnaires until 8.8.2026, and the redirect that stood in for it is gone.
//   * THE GATES. Every row is behind the `can(...)` it was behind before, and no row's set is widened to
//     make a section simpler. A SECTION is rendered when the caller holds at least one of its rows'
//     capabilities — the union, written out — so a section can never be a heading with nothing under it,
//     and a lone row can never be hidden because its neighbours are somebody else's.
export function AppLayout() {
  const { t } = useTranslation();
  const { can } = useCapabilities();

  // Each section's own gate: the UNION of its rows' capabilities, so the heading appears exactly when
  // something appears under it. "Omat" has none — its first row is every signed-in person's.
  const showCourses =
    can("contentManagement") ||
    can("trainingAssignment") ||
    can("certificateOperations") ||
    can("complianceReporting");

  // `questionnaireManagement` { ADMIN, HR } is a SUBSET of `questionnaireReportRead`
  // { ADMIN, HR, JOHDON_RAPORTTI }, so the second term is what actually widens this section — and it is
  // why an esimies-level johdon raporttikäyttäjä sees the section at all: for them it holds exactly one
  // row, Johdon raportit, which is the only surface in the engine that would answer them anything.
  const showQuestionnaires = can("questionnaireManagement") || can("questionnaireReportRead");

  const showPeople =
    can("userAdministration") ||
    can("trainingAssignment") ||
    can("employmentRecordView") ||
    can("viewScopeGrantManagement");

  const showAdmin = can("auditRead") || can("supportAccessVisibility");

  return (
    // The tenant pill — WHICH ORGANISATION this page is. Since the owner's decision of 3.11.2026 it is
    // drawn for EVERY signed-in reader, with the organisation's logo when one is stored, because a person
    // who cannot see whose system they are in cannot tell two customers' screens apart. For a reader whose
    // token carries memberships it is also the switch it has been since ADR-0018 §6 — one element, so the
    // name is never in the bar twice (auth/tenantPicker.tsx `TenantIdentity`). The product brand is
    // untouched and stays in the sidebar head.
    <AppShell brandName={t("app.title")} tenant={<TenantIdentity />} nav={
      <>
        {/* OMAT — the signed-in person's own things, whatever else they are in this tenant. The first
            three open the learner's own page: "/" on its Koulutukset tab, "/certificates" on Todistukset,
            "/questionnaires" on Kyselyt. Two ways to one view is deliberate (14.9.2026) — the rail is a
            shortcut, the tab strip says where you are. "/" is `end`-matched so it stops highlighting on
            the other two; without it every row would be active at once. The others are prefix-matched on
            purpose, so the row stays lit on the detail each one leads to — /certificates/:attemptId,
            /questionnaires/:id/respond.

            Omat toimenpiteet joins them here (3.11.2026) rather than staying under Muutosjohtaminen: its
            reader is an ordinary employee of the customer who administers nothing in the module and may
            open exactly that one surface in it (ADR-0016 §4.5 property 3, `/api/my/change-actions`). It is
            the same shape as the three above — a list of what is asked of ME — and that is what the
            section is named after. It keeps its own `changeOwnActions` gate, so it appears for its holder
            and for nobody else; the section itself needs no gate, because the row above it is every
            signed-in person's. */}
        <div className="app-nav__section">
          <p className="app-nav__section-title">{t("nav.section.own")}</p>
          <NavEntry to="/" end icon="myTrainings" label={t("nav.myTrainings")} />
          <NavEntry to="/certificates" icon="certificates" label={t("nav.certificates")} />
          <NavEntry to="/questionnaires" icon="myQuestionnaires" label={t("nav.myQuestionnaires")} />
          {can("changeOwnActions") && (
            <NavEntry
              to="/change-actions"
              data-testid="nav-change-actions"
              icon="changeActions"
              label={t("nav.changeActions")}
            />
          )}
        </div>

        {/* KOULUTUKSET — the training side of the catalogue and everything done with it.

            KURSSIT is the row "Sisällönhallinta" became, and it is now gated on `contentManagement`
            ALONE. That is not a narrowing of anybody's authority: the old row was behind
            `contentManagement || questionnaireManagement` because the page behind it carried both lists,
            and the questionnaire half of it is the Kyselyt row in the next section, behind the capability
            it always needed. An HR user holds `questionnaireManagement` and not `contentManagement`, so
            they lose a row that opened a page whose course half they could never use, and keep the list
            they came for under its own name.

            The three rows under it are what a person DOES with a course — assign it, keep its
            certificates, read who has completed it. Each keeps its own capability, and the three sets are
            genuinely different: `trainingAssignment` is { ADMIN, HR }, `certificateOperations` is
            { ADMIN } (the endpoint group's own gate) and `complianceReporting` adds ESIMIES, whose holder
            sees this section for the coverage report alone. */}
        {showCourses && (
          <div className="app-nav__section">
            <p className="app-nav__section-title">{t("nav.section.courses")}</p>
            {can("contentManagement") && (
              <NavEntry to="/admin/courses" icon="courses" label={t("nav.courses")} />
            )}
            {can("trainingAssignment") && (
              <NavEntry to="/admin/assign" icon="assign" label={t("nav.assign")} />
            )}
            {/* Todistusten ylläpito (E6 / US-6.1) — the tenant's failed and stuck certificate renders,
                and the way in to a learner's certificate. Its own capability ({ ADMIN }, the endpoint
                group's own gate), not `userAdministration`: see capabilities.ts for why two sets that
                hold the same role today stay two sets. */}
            {can("certificateOperations") && (
              <NavEntry
                to="/admin/certificates"
                data-testid="nav-certificate-ops"
                icon="certificateOps"
                label={t("nav.certificateOps")}
              />
            )}
            {can("complianceReporting") && (
              <NavEntry to="/reports/coverage" icon="reports" label={t("nav.reports")} />
            )}
          </div>
        )}

        {/* KYSELYT — the questionnaire engine, from the list to the two reports read off it.

            KYSELYT, the row, leads to /admin/questionnaires: the engine's own management list, which
            answered that path until 8.8.2026, moved onto Sisällönhallinta as a tab, and is a page again.
            The route is prefix-matched (no `end`), so the row stays lit on every surface of one
            questionnaire — the editor, the participation report, the result report, the quote workspace,
            the settings — which all hang under /admin/questionnaires/:id.

            KYSELYN TULOKSET keeps BOTH conditions it had: the section's `questionnaireReportRead` and its
            own `questionnaireManagement`. That is not redundancy — the first is the section's gate and the
            second is narrower inside it, so a JOHDON_RAPORTTI holder (report-read, no management) does not
            see a row whose every read would answer them 403.

            JOHDON RAPORTIT is under the section's own gate and nothing more, which is what makes the
            section the right home for it: for the leadership reader it is the one row they have, under a
            heading naming the thing they are reading about rather than an authority they do not hold. */}
        {showQuestionnaires && (
          <div className="app-nav__section">
            <p className="app-nav__section-title">{t("nav.section.questionnaires")}</p>
            {can("questionnaireManagement") && (
              <NavEntry
                to="/admin/questionnaires"
                data-testid="nav-questionnaires"
                icon="questionnaires"
                label={t("nav.questionnaires")}
              />
            )}
            {can("questionnaireReportRead") && can("questionnaireManagement") && (
              <NavEntry
                to="/reports/questionnaires"
                data-testid="nav-questionnaire-results"
                icon="questionnaireResults"
                label={t("nav.questionnaireResults")}
              />
            )}
            {can("questionnaireReportRead") && (
              <NavEntry
                to="/reports/leadership"
                data-testid="nav-leadership-reports"
                icon="leadershipReports"
                label={t("nav.leadershipReports")}
              />
            )}
          </div>
        )}

        {/* MUUTOSJOHTAMINEN (ADR-0016) — the consultant's module, and the section whose readers are the
            sharpest case for naming sections after their subject: `changeProgramManagement` is the
            vastuukonsultti ALONE, and §4.5 property 1 keeps the tenant ADMIN deliberately out of it, so
            this section's reader administers nothing in this tenant.

            The gate is the UNION of the two rows' capabilities, because the second row has a reader the
            first does not. A holder of `MJ_JOHDON_RAPORTTI` alone may open exactly one surface in the
            whole module (the published muutoskartta, §4.5 property 2), so gating the section on
            `changeProgramManagement` would hide the only row they have. The CONSULTANT holds both.

            Muutoskartta is listed SECOND although it is the module's widest view, because the rail is
            ordered by what its reader does rather than by breadth: the consultant arrives to work on a
            programme, and the map is where they go to see them all at once. For the leadership reader the
            question does not arise — the row above is not rendered for them at all.

            Omat toimenpiteet is NOT here any more (3.11.2026): it moved to Omat, where the note above
            says why. The section's gate lost `changeOwnActions` with it, and had to — a heading reading
            "Muutosjohtaminen" over no row at all is what an action owner would otherwise see. */}
        {(can("changeProgramManagement") || can("changePublishedReportRead")) && (
          <div className="app-nav__section">
            <p className="app-nav__section-title">{t("nav.section.changeManagement")}</p>
            {can("changeProgramManagement") && (
              <NavEntry
                to="/change-programs"
                data-testid="nav-change-programs"
                icon="changePrograms"
                label={t("nav.changePrograms")}
              />
            )}
            {can("changePublishedReportRead") && (
              <NavEntry
                to="/change-map"
                data-testid="nav-change-map"
                icon="changeMap"
                label={t("nav.changeMap")}
              />
            )}
          </div>
        )}

        {/* HENKILÖSTÖ JA ORGANISAATIO — the people of this tenant and the structure they sit in.

            RYHMÄT is here and not under Koulutukset, although its capability is `trainingAssignment`: a
            group is a set of PEOPLE, reusable by anything that addresses people, and the authority that
            happens to gate it today is not what the row is about. The owner chose this placement with the
            proposal (3.11.2026).

            TYÖSUHDETIEDOT (ADR-0020 §4, §16 task 5) loses the section of its own it had, and is a row
            here. Its gate is `employmentRecordView` — the tenant ADMIN AND the HRM expert (§5) — and NOT
            `hrmPersonnelRecord`, the narrower of the two: the row opens the record LIST, while the two
            reveals and the work permits are gated inside the record page itself (§6 point 6), because one
            page has two authorities over parts of it and a rail cannot say that. For an Incedo HRM expert
            this is the only row in the section, which is the arrangement a subject-named heading makes
            honest: they are reading about people, and they administer nothing.

            NÄKYMÄLAAJUUDET (FR-Q.48) is here rather than under Kyselyt, although what it widens is a
            questionnaire report: it is an act performed ON somebody, not something its holder reads — the
            admin who presses it is deciding how wide somebody else's report will be. Its own capability,
            not `userAdministration`, which is ADMIN alone — the HR expert the requirement names holds
            this one. */}
        {showPeople && (
          <div className="app-nav__section">
            <p className="app-nav__section-title">{t("nav.section.people")}</p>
            {can("userAdministration") && (
              <NavEntry to="/admin/users" icon="users" label={t("nav.users")} />
            )}
            {can("trainingAssignment") && (
              <NavEntry to="/admin/groups" icon="groups" label={t("nav.groups")} />
            )}
            {can("userAdministration") && (
              <NavEntry to="/admin/organization" icon="organization" label={t("nav.organization")} />
            )}
            {can("employmentRecordView") && (
              <NavEntry
                to="/hrm/employment-records"
                data-testid="nav-employment-records"
                icon="employmentRecords"
                label={t("nav.employmentRecords")}
              />
            )}
            {can("viewScopeGrantManagement") && (
              <NavEntry
                to="/admin/view-scopes"
                data-testid="nav-view-scopes"
                icon="viewScopes"
                label={t("nav.viewScopes")}
              />
            )}
          </div>
        )}

        {/* YLLÄPITO — the two rows that are about the SYSTEM rather than about courses, questionnaires or
            people: what has happened in this tenant, and who from Verso can currently see it.

            They sit side by side and stay two rows: the chain answers "what happened, in order" and
            Verson tukipääsy (ADR-0018 §8) answers "does Verso have access to us right now", which is the
            question a pääkäyttäjä opens a screen to ask. Their capabilities differ by exactly one role —
            `auditRead` adds COMPLIANCE_AUDITOR, who reads the chain, while acting on a live window is the
            pääkäyttäjä's — so an auditor sees this section holding the one row that is theirs. */}
        {showAdmin && (
          <div className="app-nav__section">
            <p className="app-nav__section-title">{t("nav.section.admin")}</p>
            {can("auditRead") && (
              <NavEntry to="/admin/audit" icon="audit" label={t("nav.audit")} />
            )}
            {can("supportAccessVisibility") && (
              <NavEntry
                to="/admin/support-access"
                data-testid="nav-support-access"
                icon="audit"
                label={t("nav.supportAccess")}
              />
            )}
          </div>
        )}
      </>
    } />
  );
}
